import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";
import reports from "./ReportConfig";
import ReportCard from "./ReportCard";
import config from "../config";
import WhatsNewSection from "./WhatsNewSection";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || "myBOQ";
const SHOW_FREE_CREDIT = process.env.REACT_APP_SHOW_FREE_CREDIT === "true";
const FREE_CREDIT_COUNT = Number(process.env.REACT_APP_FREE_CREDIT_COUNT || 1);
const SHOW_DPIIT = process.env.REACT_APP_SHOW_DPIIT === "true";

const COMBINED_BADGE = "/badges/startup-india-dpiit.png";
const DPIIT_BADGE = "/badges/dpiit.png";

const partners = [
  {
    name: "CEIIT",
    image: "/CEIIT_LOGO.png",
    description: "Official training and ecosystem partner for estimation skill development.",
    link: "https://civilengineeringiit.in/",
  },
];

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const HeroSection = ({ openContactModal }) => {
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const goSignupSameTab = (placement = "hero") => {
    track("apply_cta_click", {
      placement,
      target: "signup",
      mode: "external_same_tab",
    });
    window.location.assign(config.signUpUrl);
  };

  const roles = useMemo(
    () => [
      {
        key: "engineers",
        icon: "/engineer.svg",
        label: "Engineers",
        title: "For Engineers",
        bullets: [
          "Create SSR-based estimates quickly with accurate quantities and standardized outputs.",
          "Generate PWD Estimate + Schedule B for approvals and submissions.",
          "Reuse revisions without re-entering the full estimate.",
        ],
        cta: { text: "Start Estimation", to: "/signup" },
      },
      {
        key: "contractors",
        icon: "/contractor.svg",
        label: "Contractors",
        title: "For Contractors",
        bullets: [
          "Prepare tender-ready BOQ faster with fewer manual checks.",
          "Track revisions and re-generate reports in minutes.",
          "Reduce Excel dependency and avoid calculation mistakes.",
        ],
        cta: { text: "Get Started", to: "/signup" },
      },
      {
        key: "architects",
        icon: "/architect.svg",
        label: "Architects",
        title: "For Architects",
        bullets: [
          "Convert drawings and measurements into a clean BOQ structure.",
          "Share professional reports with clients and consultants.",
          "Maintain scope changes easily using revisions.",
        ],
        cta: { text: "Explore Features", to: "/features" },
      },
      {
        key: "municipal",
        icon: "/municipality.svg",
        label: "Municipal Staff",
        title: "For Municipal & Approval Teams",
        bullets: [
          "Review SSR items, abstracts, and recap faster during approvals.",
          "Standardized formats reduce verification time and rework.",
          "Keep estimate records organized for audits and future reference.",
        ],
        cta: { text: "See Reports", to: "/features" },
      },
      {
        key: "consultants",
        icon: "/consultant.svg",
        label: "Consultants",
        title: "For Consultants",
        bullets: [
          "Prepare rate analysis and abstracts with consistent outputs.",
          "Deliver export-ready reports for clients without formatting work.",
          "Manage multiple projects with reusable templates and revisions.",
        ],
        cta: { text: "Explore Features", to: "/features" },
      },
    ],
    []
  );

  const openRoleModal = (role) => {
    setActiveRole(role);
    setRoleModalOpen(true);
    track("professional_card_open", { placement: "hero", role: role?.key });
  };

  const closeRoleModal = () => {
    setRoleModalOpen(false);
    track("professional_card_close", {
      placement: "hero",
      role: activeRole?.key,
    });
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeRoleModal();
    };

    if (roleModalOpen) {
      document.addEventListener("keydown", onKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [roleModalOpen, activeRole]);

  return (
    <section className="bg-white py-12 px-6 lg:px-20">
      <SEO
        title={`${WEBSITE_NAME} | Digital Infrastructure Estimation Platform (SSR)`}
        description="myBOQ is a digital infrastructure estimation platform built for engineers, contractors, consultants, and public agencies. Create SSR-compliant, audit-ready estimates and standardized reports faster."
        keywords="infrastructure estimation platform, civil estimation, SSR, PWD SSR, tender BOQ, schedule b, rate analysis, abstract, recap sheet, Maharashtra, India, myboq, siliconmount"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={BASE_URL}
      />

      {/* HERO */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
        {/* Left: Messaging */}
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-700">
              🇮🇳 India-focused • MH PWD SSR-ready • Infrastructure-first
            </span>

            {SHOW_DPIIT && (
              <span className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-full text-xs font-semibold text-orange-800">
                🚀 Product by DPIIT-Recognized Startup: SiliconMount
              </span>
            )}
          </div>

          <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
            <span className="text-orange-600">Standardizing</span> Infrastructure{" "}
            Estimation for <span className="text-orange-600">India</span>
          </h1>

          <p className="text-gray-700 text-lg leading-relaxed max-w-2xl">
            myBOQ is a digital infrastructure estimation platform for engineers, contractors,
            consultants, and public-sector teams to create SSR-compliant, standardized,
            audit-ready estimates and reports faster.
          </p>

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-full text-sm font-semibold text-gray-800">
              ✅ SSR-Compliant Outputs
            </span>
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-full text-sm font-semibold text-gray-800">
              🏛️ Government-Ready Reports
            </span>
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-full text-sm font-semibold text-gray-800">
              ☁️ Cloud-Based Platform
            </span>
            <span className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-full text-sm font-semibold text-gray-800">
              🤖 AI-Ready Roadmap
            </span>
          </div>

          <div className="text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md">
              🇮🇳 <span className="font-semibold">Currently supports MH PWD SSR</span>
            </span>
            <span className="ml-2">Additional departments and states are planned.</span>
          </div>

          {SHOW_FREE_CREDIT && (
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
              <span aria-hidden="true">🎁</span>
              <span>
                {FREE_CREDIT_COUNT} free credit{FREE_CREDIT_COUNT > 1 ? "s" : ""} on first-time signup
              </span>

              <button
                type="button"
                className="ml-2 text-orange-700 underline hover:text-orange-900"
                onClick={() => {
                  track("free_credit_badge_click", {
                    placement: "hero",
                    credits: FREE_CREDIT_COUNT,
                  });
                  goSignupSameTab("free_credit_badge");
                }}
              >
                Claim →
              </button>
            </div>
          )}

          <div className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                className="w-full inline-flex justify-center items-center bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition duration-300 shadow-sm"
                onClick={() => {
                  track("request_demo_click", {
                    placement: "hero_primary",
                    target: "contact_modal",
                  });
                  if (openContactModal) openContactModal();
                }}
              >
                Request Demo
              </button>

              <button
                type="button"
                className="w-full inline-flex justify-center items-center bg-gray-900 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-black transition duration-300"
                onClick={() => {
                  track("apply_cta_click", {
                    placement: "hero_primary",
                    target: "signup",
                  });
                  goSignupSameTab("hero_get_started_free");
                }}
              >
                Get Started Free
              </button>

              <Link
                to="/public-sector"
                className="w-full inline-flex justify-center items-center bg-white border border-gray-200 text-gray-900 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-orange-50 transition duration-300"
                onClick={() => {
                  track("gov_institution_cta", {
                    placement: "hero_primary",
                    target: "public_sector",
                  });
                }}
              >
                For Government & Institutions
              </Link>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <a
              href="https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold text-gray-700 hover:text-orange-600 transition"
              onClick={() =>
                track("demo_video_click", {
                  placement: "hero_secondary",
                  provider: "youtube",
                })
              }
            >
              ▶ Watch Demo
            </a>

            <span className="text-gray-300 hidden sm:inline">|</span>

            <a
              href={`${BASE_URL}/myBOQ_Information_Brochure_v2.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold text-gray-700 hover:text-orange-600 transition"
              onClick={() =>
                track("brochure_download", {
                  placement: "hero_secondary",
                  file: "myBOQ_Information_Brochure_v2.pdf",
                })
              }
            >
              📄 Download Brochure
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            {SHOW_DPIIT && (
              <img
                src={COMBINED_BADGE}
                alt="Startup India + DPIIT Recognized badge"
                className="h-8 w-auto object-contain"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = DPIIT_BADGE;
                }}
              />
            )}
          </div>
        </div>

        {/* Right: Enterprise Summary Rail */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-start gap-4">
              <img
                src={partners[0].image}
                alt={partners[0].name}
                className="w-14 h-14 object-contain bg-gray-50 rounded-xl border border-gray-200 p-2 flex-shrink-0"
                loading="lazy"
              />

              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1">
                  Training & Ecosystem Partner
                </div>
                <div className="text-lg font-bold text-gray-900">{partners[0].name}</div>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  {partners[0].description}
                </p>

                <a
                  href={partners[0].link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex mt-3 text-sm font-semibold text-orange-600 hover:underline"
                  onClick={() =>
                    track("partner_visit", {
                      name: partners[0].name,
                      placement: "hero_partner",
                    })
                  }
                >
                  Visit {partners[0].name} →
                </a>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 shadow-sm">
            <div className="grid gap-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1">
                  Built for Public Infrastructure
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Standardized outputs for planning, approvals, and audits
                </h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Designed to support verification-friendly formats across PWD estimates,
                  schedules, abstracts, and recap workflows.
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {["PWD Estimates", "Schedule B", "Abstract & Recap"].map((item) => (
                    <span
                      key={item}
                      className="text-xs font-semibold bg-white text-gray-700 px-2.5 py-1 rounded-full border border-gray-200"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1">
                  AI & Innovation
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  AI roadmap for smarter estimation workflows
                </h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Smarter search, validation, and assisted estimation features are on the roadmap.
                </p>

                <Link
                  to="/ai"
                  className="inline-flex mt-3 text-sm font-semibold text-orange-600 hover:underline"
                  onClick={() =>
                    track("nav_click", { item: "ai", placement: "hero_roadmap" })
                  }
                >
                  Learn more →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHAT’S NEW */}
      <WhatsNewSection />

      {/* REPORTS AT A GLANCE */}
      <div className="mt-10 max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Reports at a Glance</h3>
          <Link
            to="/features"
            className="text-sm font-semibold text-orange-600 hover:underline"
            onClick={() =>
              track("nav_click", { item: "features", placement: "reports_glance" })
            }
          >
            View all reports →
          </Link>
        </div>

        <div className="flex flex-wrap gap-4">
          {reports.map((report, idx) => (
            <ReportCard
              key={idx}
              {...report}
              compact
              onTrack={(action, props) =>
                track(action, {
                  placement: "reports_glance",
                  report: report?.title,
                  ...props,
                })
              }
            />
          ))}
        </div>
      </div>

      {/* BUILT FOR PROFESSIONALS */}
      <div className="mt-16 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Built For Professionals
          </h2>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Tap a role to see how myBOQ supports real-world estimation workflows.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
          {roles.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => openRoleModal(item)}
              className="group rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 text-left"
              aria-label={`Open details for ${item.label}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100">
                  <img
                    src={item.icon}
                    alt={`${item.label} icon`}
                    className="h-7 w-7 object-contain"
                    loading="lazy"
                  />
                </div>

                <div className="min-w-0">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                    {item.label}
                  </h4>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ROLE MODAL */}
      {roleModalOpen && activeRole && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Professional workflow details"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRoleModal();
          }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <img src={activeRole.icon} alt="" className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">{activeRole.title}</div>
                  <div className="text-xs text-gray-500">
                    {activeRole.label} • myBOQ
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeRoleModal}
                className="h-10 w-10 rounded-full hover:bg-gray-100 text-gray-700 text-xl flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5">
              <ul className="space-y-3">
                {activeRole.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-gray-700 text-sm">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                {activeRole.cta.to === "/signup" ? (
                  <button
                    type="button"
                    className="inline-flex justify-center items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-xl transition"
                    onClick={() => {
                      track("professional_modal_cta", {
                        role: activeRole.key,
                        target: "signup",
                      });
                      goSignupSameTab("professional_modal");
                    }}
                  >
                    {activeRole.cta.text}
                  </button>
                ) : (
                  <Link
                    to={activeRole.cta.to}
                    className="inline-flex justify-center items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-xl transition"
                    onClick={() =>
                      track("professional_modal_cta", {
                        role: activeRole.key,
                        target: activeRole.cta.to,
                      })
                    }
                  >
                    {activeRole.cta.text}
                  </Link>
                )}

                <button
                  type="button"
                  onClick={() => {
                    track("professional_modal_demo", { role: activeRole.key });
                    window.location.assign("https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s");
                  }}
                  className="inline-flex justify-center items-center bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold px-5 py-3 rounded-xl transition"
                >
                  Watch Demo
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-4">
                Tip: press ESC or tap outside to close.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;